/** The Builder step panel (ADR-0071 phase 3): Read and Edit, in plain words, for every step type. */
export default {
  en: {
    mode: { label: "Show this step", read: "Read", edit: "Edit" },
    aiCall: {
      title: "Does what ‘{{step}}’ asked for:",
      what: "runs the action the AI picked, with the values it wrote.",
      whatAny: "runs the action an AI step picked, with the values it wrote.",
      choices: "The AI may choose:",
      choicesNone: "The AI step offers no systems, so this step never runs.",
      handles: "This step carries out “{{action}}” when the AI picks it.",
      notConnected: "Not connected",
      notOffered: "not offered by this system",
    },
    notConnected: {
      system: "Not connected: {{name}}",
      action: "Not connected: {{name}}",
      systemWhy: "“{{name}}” isn't connected in your organisation, so this step can't run. Pick a connected system from the list.",
      searchWhy: "To look something up in your documents, use a “Search knowledge” step instead.",
      actionWhy: "This system doesn't offer “{{name}}”. Pick one of its actions.",
    },
    read: {
      fromRun: "Values from your last test run.",
      noRun: "Run a test in Try it to see real values here.",
      nothingRead: "Nothing from earlier steps.",
      nothingMade: "Nothing that later steps use.",
      showInternal: "Show internal details ({{count}})",
      hideInternal: "Hide internal details",
      internalHelp: "Bookkeeping the agent uses itself. You don't need these to build or read the agent.",
      notInRun: "not in the test run",
      empty: "(empty)",
      viewCode: "View code ({{file}})",
      editStep: "Edit this step",
      fixedShape: "Always the same shape: {{what}}",
      seesHistory: "The conversation so far",
      seesResults: "Results of earlier steps",
      seesAttachments: "Attached documents",
      seesOn: "yes",
      context: "what the AI also sees",
      yes: "yes",
      no: "no",
      items_one: "{{count}} item",
      items_other: "{{count}} items",
    },
    flow: {
      receives: "Receives",
      gives: "Gives to the next step",
      answerText: "The text the AI writes. The next step reads it, and the person sees it when the agent finishes.",
      then: "Then goes to",
      fromStep: "from step {{n}}, ‘{{step}}’",
      fromRequest: "from the request",
      fromUnknown: "not made by any earlier step",
      setHere: "typed in this step",
      usedAs: "used as “{{name}}”",
      changeIn: "Change what it receives in “{{group}}” below",
      available_one: "Available from earlier steps ({{count}})",
      available_other: "Available from earlier steps ({{count}})",
      availableHelp: "Values this step could use. Pick one in the settings below, or use “Insert a value”.",
      availableNone: "Nothing yet: this is the first step, so it only has the request.",
      selectArrow: "Show this path",
      nameOfResultHelp: "Later steps and arrows call it “{{name}}”. Type a name below to change it.",
      technical: "Technical name",
      technicalHelp: "How later steps refer to it in the agent file. Change it only if you also change the steps that read it.",
      searchHowTitle: "How it searches:",
      searchHow: "finds the passages closest in meaning to the search text (using a small <term>embedding model</term>, not the AI model that writes answers) and that share its words, then passes the best {{n}} on. It does not write anything.",
      nameNow: "Its name now is “{{name}}”. Type a name to replace it.",
      builtIn: { today: "Today's date", weekday: "Today's weekday", now: "The time now", attachments: "Attached documents" },
    },
    group: {
      name: { t: "Name", why: "What people see on the graph and in run history." },
      aiDoes: { t: "What the AI does", why: "Write the instructions here, or use a saved prompt from the prompt library." },
      aiGives: { t: "What it gives back", why: "Text, or fields that later steps can pick one by one." },
      aiSystems: { t: "Systems the AI may use", why: "The AI can ask to use these. An arrow and a “Use a system” step must handle its choice." },
      aiFails: { t: "If it fails, and time limit", why: "What happens when the AI doesn't answer in time or its answer can't be read." },
      sysFails: { t: "If it fails", why: "What happens when the system doesn't answer or refuses: try again, then go to another step." },
      codeRuns: { t: "What it runs", why: "The function from the agent's own Python file that this step runs." },
      codeReads: { t: "What it reads", why: "Each value the code gets, and where it comes from." },
      gives: { t: "What it gives back", why: "Describes the result, so later steps can pick its fields. Change it only when you change the code." },
      limits: { t: "Safety limits", why: "Stops the step if it hangs or misbehaves." },
      webPages: { t: "Which pages", why: "A fixed address, or one from an earlier step. Only allowed websites open." },
      webRead: { t: "How to read them", why: "Read the page text, or run a page script that picks out fields." },
      sysUses: { t: "What it uses", why: "The connected system and the action to take." },
      sysSends: { t: "What it sends", why: "Each value is typed here, or taken from an earlier step." },
      sysGives: { t: "What it gives back", why: "The system describes its result. Narrow it only if later steps need fewer fields." },
      kbSearch: { t: "What to search", why: "The knowledge collection, and what to look for in it." },
      approveWhat: { t: "What to approve", why: "The value the approver sees and decides on." },
      approveWho: { t: "Who approves, and how long", why: "People with this role get the request in their inbox." },
      approveReject: { t: "If rejected", why: "Go back to a step (for example to try again), or finish." },
      askQuestion: { t: "Question", why: "Asked to the person in the chat; the run waits for the answer." },
      askAnswer: { t: "Kind of answer", why: "Later steps get the answer in this form." },
      paths: { t: "Paths", why: "The first path whose rule holds is taken; otherwise the last one." },
      decide: { t: "How the AI decides", why: "The question, the possible answers and what happens when the AI isn't sure." },
      together: { t: "Steps that run at the same time", why: "They start together; the run continues when all have finished." },
      combine: { t: "How to combine the results", why: "What the step after them receives." },
      agent: { t: "Which agent", why: "Hands this part of the job to an agent you already have." },
      builtIn: { t: "Built-in code", why: "Platform code, set by platform engineers only." },
      connect: { t: "Add a path", why: "Draw an arrow on the graph, or pick the next step here." },
      decideHow: { t: "How to decide", why: "By rules you write, or let the AI choose. Switching keeps the steps it can go to." },
      wait: { t: "Wait for all", why: "The wait step of ‘{{step}}’: the run goes on when all its steps have finished." },
      advanced: "Advanced (rarely changed)",
    },
    f: {
      labelEn: "Name (English)",
      labelAr: "Name (Arabic)",
      descEn: "One line about what it does (English)",
      descAr: "One line about what it does (Arabic)",
      makesEn: "Name of what it makes (English)",
      makesAr: "Name of what it makes (Arabic)",
      promptMode: "Instructions",
      promptWrite: "Write instructions",
      promptSaved: "Use a saved prompt",
      instructions: "What the AI should do",
      instructionsHelp: "Type { to insert a value from earlier steps.",
      promptRef: "Saved prompt",
      promptRefHelp: "Pick a prompt from the library (Builder › Saved prompts) and which version to use. Its values are marked {{name}}; say what each one takes.",
      givesMode: "Gives back",
      givesText: "Text",
      givesFields: "Fields later steps can use",
      systems: "Systems",
      onError: "If the AI fails",
      onErrorFail: "Stop the run",
      onErrorContinue: "Continue (a later step can fall back)",
      timeout: "Time limit (seconds)",
      timeoutDefault: "Platform default",
      seesHistory: "Sees the conversation so far",
      seesResults: "Sees the results of earlier steps",
      seesAttachments: "Sees attached documents",
      model: "AI model",
      modelDefault: "The agent's model ({{alias}})",
      temperature: "Creativity (0 exact, 1 creative)",
      maxTokens: "Longest answer (tokens)",
      timeZone: "Time zone for “today” and “now”",
      timeZoneDefault: "UTC",
      file: "File",
      fn: "Function",
      openCode: "Open code",
      inline: "A short program written in this step",
      resultFormat: "Result format",
      resultJson: "Fields (JSON)",
      resultText: "Text",
      memory: "Memory (MB)",
      internet: "Internet",
      internetNone: "None (code never goes online)",
      howRead: "How it reads",
      readOne: "One page",
      readMany: "Several pages",
      interact: "Click and type on a page",
      searchSite: "Search a website",
      submitForm: "Fill in and send a form",
      downloadFile: "Download a file into Documents",
      web: {
        site: "Website",
        sitePick: "Choose one of your organisation's websites",
        noSites: "Your organisation has no websites for agents yet. An organisation admin adds them in Administration › Web browser.",
        manage: "Manage websites",
        askAdmin: "An organisation admin manages them in Administration › Web browser.",
        allows: "Allowed there: {{list}}",
        notAllowed: "{{host}} does not allow “{{what}}” for your organisation, so this step would be refused. Ask an organisation admin to allow it (Administration › Web browser).",
        notOnList: "{{host}} is not one of your organisation's websites, so this step would be refused.",
        notOpen: "{{host}} is allowed but not open yet: the platform team still has to let it through.",
        what: {
          read: "Only reads pages.",
          search: "Only reads: it searches with the site's own search box and reads the results.",
          forms: "Sends a form on {{host}}: a change on that website. Your approval rules apply (usually a person approves first), and it is sent only once.",
          download: "Keeps the file in Documents (only documents, tables and pictures, up to 5 MB). It can be undone.",
          interact: "Clicks and types on the page. A person always approves first.",
        },
      },
      script: "Page script",
      noScript: "The page text",
      allowed: "Allowed websites are set by the administrator in Administration › Tools.",
      changesData: "Changes data in this system: it can be put behind an approval.",
      readsOnly: "Only reads from this system.",
      topK: "Up to {{n}} passages (set in About this agent).",
      firstStep: "Make it the first step",
      isFirst: "This is the first step",
      delete: "Delete step",
      savedAs: "Saved in the notebook as",
      savedAsHelp: "The name later steps use to read this result. Empty means “{{fallback}}”.",
      keepOnly: "Keep only this part of the result",
      keepOnlyHelp: "Leave empty to keep the whole result.",
      keepOnlyCode: "The code runner returns the result and its logs; “result” keeps only the result.",
      stdin: "Text input for the code",
      rawJson: "Edit all values as JSON",
      rawJsonHelp: "For developers: every value of this step, as the agent file stores it.",
      techId: "Technical id",
      repeat: "Repeat at most",
      onLimit: "If the limit is reached, go to",
      onLimitStop: "Stop and keep the last answer",
      retries: "Try again",
      retriesHelp: "How many more times to try a failed call (0 to 3), waiting 2, 4 and then 8 seconds. A call the rules refused or a person rejected is not tried again.",
      failTo: "If it still fails, go to",
      failToHelp: "Later steps can read what went wrong as “What went wrong” in this step's result.",
      failToNone: "Stop here and tell the person (a step that only reads goes on)",
      onLimitHelp: "Where the run goes when this step would run once more than allowed, e.g. a step that answers with the last draft. If you keep “Stop”, the run ends with a plain message and keeps the last answer it gave.",
      onLimitNeedsRepeat: "Set “Repeat at most” first.",
      askWho: "Who answers",
      askWhoNone: "The person who asked",
      askWhoHelp: "Empty: the person who started the request answers in the chat. Add roles and only people with them answer, in the Reviewer inbox; the person who asked never sees the question. Runs started without a person (a schedule) go to approvers.",
      askRequesterToo: "The person who asked may answer too",
      connectTo: "Next step",
      connect: "Add path",
      newValue: "Name of a new value",
      addValue: "Add value",
      removeValue: "Remove {{name}}",
      strategy: { merge: "Keep each result under its own name", list: "A list of all results", vote: "The most common answer" },
      byRule: "By a rule",
      byAi: "The AI decides",
      addWait: "Add the wait step",
      noWait: "There is no wait step yet: the run can't go on after these steps.",
      continues: "The run goes on from ‘{{step}}’ when all of them have finished.",
      waitOf: "Set the steps in ‘{{step}}’",
      fixedMakes: "This step always makes the same shape; nothing to set.",
    },
  },
  ar: {
    mode: { label: "عرض هذه الخطوة", read: "قراءة", edit: "تعديل" },
    aiCall: {
      title: "تنفّذ ما طلبته ‘{{step}}’:",
      what: "تشغّل الإجراء الذي اختاره الذكاء الاصطناعي، بالقيم التي كتبها.",
      whatAny: "تشغّل الإجراء الذي اختارته خطوة ذكاء اصطناعي، بالقيم التي كتبتها.",
      choices: "يمكن للذكاء الاصطناعي أن يختار:",
      choicesNone: "لا تتيح خطوة الذكاء الاصطناعي أي أنظمة، لذا لا تعمل هذه الخطوة أبداً.",
      handles: "تنفّذ هذه الخطوة «{{action}}» عندما يختاره الذكاء الاصطناعي.",
      notConnected: "غير متصل",
      notOffered: "لا يقدّمه هذا النظام",
    },
    notConnected: {
      system: "غير متصل: {{name}}",
      action: "غير متصل: {{name}}",
      systemWhy: "«{{name}}» غير متصل في مؤسستك، لذا لا يمكن تشغيل هذه الخطوة. اختر نظاماً متصلاً من القائمة.",
      searchWhy: "للبحث في مستنداتك، استخدم خطوة «بحث في المعرفة» بدلاً منها.",
      actionWhy: "هذا النظام لا يقدّم «{{name}}». اختر أحد إجراءاته.",
    },
    read: {
      fromRun: "القيم من آخر تشغيل تجريبي.",
      noRun: "شغّل تجربة من «جرّبه» لترى القيم الحقيقية هنا.",
      nothingRead: "لا شيء من الخطوات السابقة.",
      nothingMade: "لا شيء تستخدمه الخطوات اللاحقة.",
      showInternal: "عرض التفاصيل الداخلية ({{count}})",
      hideInternal: "إخفاء التفاصيل الداخلية",
      internalHelp: "معلومات يستخدمها الوكيل لنفسه. لا تحتاجها لبناء الوكيل أو فهمه.",
      notInRun: "ليست في التشغيل التجريبي",
      empty: "(فارغ)",
      viewCode: "عرض الشيفرة ({{file}})",
      editStep: "تعديل هذه الخطوة",
      fixedShape: "الشكل نفسه دائمًا: {{what}}",
      seesHistory: "المحادثة حتى الآن",
      seesResults: "نتائج الخطوات السابقة",
      seesAttachments: "المستندات المرفقة",
      seesOn: "نعم",
      context: "ما يراه الذكاء الاصطناعي أيضًا",
      yes: "نعم",
      no: "لا",
      items_zero: "لا عناصر",
      items_one: "عنصر واحد",
      items_two: "عنصران",
      items_few: "{{count}} عناصر",
      items_many: "{{count}} عنصرًا",
      items_other: "{{count}} عنصر",
    },
    flow: {
      receives: "تتلقى",
      gives: "تعطي للخطوة التالية",
      answerText: "النص الذي يكتبه الذكاء الاصطناعي. تقرؤه الخطوة التالية، ويراه الشخص عندما ينتهي الوكيل.",
      then: "ثم تنتقل إلى",
      fromStep: "من الخطوة {{n}}، ‹{{step}}›",
      fromRequest: "من الطلب",
      fromUnknown: "لا تُنتجها أي خطوة سابقة",
      setHere: "مكتوبة في هذه الخطوة",
      usedAs: "تُستخدم بوصفها «{{name}}»",
      changeIn: "غيّر ما تتلقاه في «{{group}}» أدناه",
      available_zero: "المتاح من الخطوات السابقة ({{count}})",
      available_one: "المتاح من الخطوات السابقة ({{count}})",
      available_two: "المتاح من الخطوات السابقة ({{count}})",
      available_few: "المتاح من الخطوات السابقة ({{count}})",
      available_many: "المتاح من الخطوات السابقة ({{count}})",
      available_other: "المتاح من الخطوات السابقة ({{count}})",
      availableHelp: "قيم يمكن لهذه الخطوة استخدامها. اختر إحداها في الإعدادات أدناه، أو استخدم «إدراج قيمة».",
      availableNone: "لا شيء بعد: هذه الخطوة الأولى، فليس لديها إلا الطلب.",
      selectArrow: "عرض هذا المسار",
      nameOfResultHelp: "تسمّيه الخطوات اللاحقة والأسهم «{{name}}». اكتب اسمًا أدناه لتغييره.",
      technical: "الاسم التقني",
      technicalHelp: "كيف تشير إليه الخطوات اللاحقة في ملف الوكيل. غيّره فقط إذا غيّرت الخطوات التي تقرؤه أيضًا.",
      searchHowTitle: "كيف تبحث:",
      searchHow: "تجد المقاطع الأقرب في المعنى إلى نص البحث (باستخدام <term>نموذج تضمين</term> صغير، لا نموذج الذكاء الاصطناعي الذي يكتب الإجابات) والتي تشاركه كلماته، ثم تمرّر أفضل {{n}} منها. لا تكتب أي شيء.",
      nameNow: "اسمها الآن «{{name}}». اكتب اسمًا ليحل محله.",
      builtIn: { today: "تاريخ اليوم", weekday: "يوم الأسبوع اليوم", now: "الوقت الآن", attachments: "المستندات المرفقة" },
    },
    group: {
      name: { t: "الاسم", why: "ما يراه الناس على المخطط وفي سجل التشغيل." },
      aiDoes: { t: "ما يفعله الذكاء الاصطناعي", why: "اكتب التعليمات هنا، أو استخدم موجّهًا محفوظًا من مكتبة الموجّهات." },
      aiGives: { t: "ما يُرجعه", why: "نص، أو حقول تختار منها الخطوات اللاحقة واحدًا واحدًا." },
      aiSystems: { t: "الأنظمة التي يمكنه استخدامها", why: "يمكن للذكاء الاصطناعي أن يطلب استخدامها. يجب أن يعالج سهمٌ وخطوةُ «استخدام نظام» اختيارَه." },
      aiFails: { t: "إذا فشل، والمهلة", why: "ما يحدث إذا لم يُجب الذكاء الاصطناعي في الوقت أو تعذّرت قراءة إجابته." },
      sysFails: { t: "إذا فشلت", why: "ما يحدث إذا لم يُجب النظام أو رفض: أعد المحاولة، ثم انتقل إلى خطوة أخرى." },
      codeRuns: { t: "ما يشغّله", why: "الدالة من ملف بايثون الخاص بالوكيل التي تشغّلها هذه الخطوة." },
      codeReads: { t: "ما يقرؤه", why: "كل قيمة تصل إلى الشيفرة، ومن أين تأتي." },
      gives: { t: "ما يُرجعه", why: "يصف النتيجة لتتمكن الخطوات اللاحقة من اختيار حقولها. غيّره فقط عند تغيير الشيفرة." },
      limits: { t: "حدود الأمان", why: "يوقف الخطوة إذا علقت أو أساءت التصرف." },
      webPages: { t: "أي الصفحات", why: "عنوان ثابت، أو عنوان من خطوة سابقة. تُفتح المواقع المسموح بها فقط." },
      webRead: { t: "كيف تُقرأ", why: "قراءة نص الصفحة، أو تشغيل نص برمجي للصفحة يستخرج الحقول." },
      sysUses: { t: "ما يستخدمه", why: "النظام المتصل والإجراء المطلوب." },
      sysSends: { t: "ما يرسله", why: "تُكتب كل قيمة هنا، أو تؤخذ من خطوة سابقة." },
      sysGives: { t: "ما يُرجعه", why: "يصف النظام نتيجته. قلّصها فقط إذا احتاجت الخطوات اللاحقة حقولًا أقل." },
      kbSearch: { t: "ما يُبحث فيه", why: "مجموعة المعرفة، وما يُبحث عنه فيها." },
      approveWhat: { t: "ما يُعتمد", why: "القيمة التي يراها المعتمِد ويقرر بشأنها." },
      approveWho: { t: "من يعتمد، وخلال كم", why: "يصل الطلب إلى صندوق من يحملون هذا الدور." },
      approveReject: { t: "إذا رُفض", why: "العودة إلى خطوة (مثلًا للمحاولة مجددًا)، أو الإنهاء." },
      askQuestion: { t: "السؤال", why: "يُطرح على الشخص في المحادثة، وينتظر التشغيل الإجابة." },
      askAnswer: { t: "نوع الإجابة", why: "تصل الإجابة إلى الخطوات اللاحقة بهذا الشكل." },
      paths: { t: "المسارات", why: "يُتّبع أول مسار تنطبق قاعدته، وإلا فالأخير." },
      decide: { t: "كيف يقرر الذكاء الاصطناعي", why: "السؤال، والإجابات الممكنة، وما يحدث إذا لم يكن متأكدًا." },
      together: { t: "خطوات تعمل في الوقت نفسه", why: "تبدأ معًا، ويستمر التشغيل عند انتهائها كلها." },
      combine: { t: "كيف تُجمع النتائج", why: "ما تتلقاه الخطوة التي بعدها." },
      agent: { t: "أي وكيل", why: "يسلّم هذا الجزء من العمل إلى وكيل لديك." },
      builtIn: { t: "شيفرة مدمجة", why: "شيفرة المنصة، يضبطها مهندسو المنصة فقط." },
      connect: { t: "إضافة مسار", why: "ارسم سهمًا على المخطط، أو اختر الخطوة التالية هنا." },
      decideHow: { t: "طريقة القرار", why: "بقواعد تكتبها، أو دع الذكاء الاصطناعي يختار. التبديل يحتفظ بالخطوات التي يمكن الذهاب إليها." },
      wait: { t: "انتظار الكل", why: "خطوة الانتظار لـ‹{{step}}›: يستمر التشغيل عند انتهاء كل خطواتها." },
      advanced: "متقدّم (نادرًا ما يتغيّر)",
    },
    f: {
      labelEn: "الاسم (بالإنجليزية)",
      labelAr: "الاسم (بالعربية)",
      descEn: "سطر عمّا تفعله (بالإنجليزية)",
      descAr: "سطر عمّا تفعله (بالعربية)",
      makesEn: "اسم ما تُنتجه (بالإنجليزية)",
      makesAr: "اسم ما تُنتجه (بالعربية)",
      promptMode: "التعليمات",
      promptWrite: "كتابة التعليمات",
      promptSaved: "استخدام موجّه محفوظ",
      instructions: "ما يجب أن يفعله الذكاء الاصطناعي",
      instructionsHelp: "اكتب { لإدراج قيمة من خطوة سابقة.",
      promptRef: "الموجّه المحفوظ",
      promptRefHelp: "اختر موجّهًا من المكتبة (المنشئ › التعليمات المحفوظة) وأي إصدار تستخدمه. قيمه معلَّمة بـ {{name}}؛ حدّد ما تأخذه كل منها.",
      givesMode: "يُرجع",
      givesText: "نصًا",
      givesFields: "حقولًا تستخدمها الخطوات اللاحقة",
      systems: "الأنظمة",
      onError: "إذا فشل الذكاء الاصطناعي",
      onErrorFail: "إيقاف التشغيل",
      onErrorContinue: "المتابعة (يمكن لخطوة لاحقة أن تتدارك)",
      timeout: "المهلة (بالثواني)",
      timeoutDefault: "الافتراضي في المنصة",
      seesHistory: "يرى المحادثة حتى الآن",
      seesResults: "يرى نتائج الخطوات السابقة",
      seesAttachments: "يرى المستندات المرفقة",
      model: "نموذج الذكاء الاصطناعي",
      modelDefault: "نموذج الوكيل ({{alias}})",
      temperature: "الإبداع (0 دقيق، 1 مبدع)",
      maxTokens: "أطول إجابة (رموز)",
      timeZone: "المنطقة الزمنية لـ«اليوم» و«الآن»",
      timeZoneDefault: "التوقيت العالمي",
      file: "الملف",
      fn: "الدالة",
      openCode: "فتح الشيفرة",
      inline: "برنامج قصير مكتوب في هذه الخطوة",
      resultFormat: "شكل النتيجة",
      resultJson: "حقول (JSON)",
      resultText: "نص",
      memory: "الذاكرة (ميغابايت)",
      internet: "الإنترنت",
      internetNone: "لا يوجد (الشيفرة لا تتصل بالإنترنت أبدًا)",
      howRead: "طريقة القراءة",
      readOne: "صفحة واحدة",
      readMany: "عدة صفحات",
      interact: "النقر والكتابة في صفحة",
      searchSite: "البحث في موقع",
      submitForm: "تعبئة نموذج وإرساله",
      downloadFile: "تنزيل ملف إلى المستندات",
      web: {
        site: "الموقع",
        sitePick: "اختر أحد مواقع جهتك",
        noSites: "ليست لدى جهتك مواقع للوكلاء بعد. يضيفها مسؤول الجهة في الإدارة › متصفح الويب.",
        manage: "إدارة المواقع",
        askAdmin: "يديرها مسؤول الجهة في الإدارة › متصفح الويب.",
        allows: "المسموح هناك: {{list}}",
        notAllowed: "لا يسمح {{host}} بـ«{{what}}» لجهتك، لذا ستُرفض هذه الخطوة. اطلب من مسؤول الجهة السماح بذلك (الإدارة › متصفح الويب).",
        notOnList: "‏{{host}} ليس من مواقع جهتك، لذا ستُرفض هذه الخطوة.",
        notOpen: "‏{{host}} مسموح به لكنه غير مفتوح بعد: ما زال على فريق المنصة السماح بالوصول إليه.",
        what: {
          read: "يقرأ الصفحات فقط.",
          search: "يقرأ فقط: يبحث بمربع البحث الخاص بالموقع ويقرأ النتائج.",
          forms: "يرسل نموذجًا في {{host}}: وهذا تغيير في ذلك الموقع. تنطبق قواعد الاعتماد لديك (عادة يعتمد شخص أولًا)، ويُرسل مرة واحدة فقط.",
          download: "يحفظ الملف في المستندات (المستندات والجداول والصور فقط، حتى 5 ميغابايت). ويمكن التراجع عنه.",
          interact: "ينقر ويكتب في الصفحة. يعتمد شخص دائمًا أولًا.",
        },
      },
      script: "نص الصفحة البرمجي",
      noScript: "نص الصفحة",
      allowed: "يحدد المسؤول المواقع المسموح بها في الإدارة › الأدوات.",
      changesData: "يغيّر بيانات في هذا النظام: يمكن وضعه خلف اعتماد.",
      readsOnly: "يقرأ فقط من هذا النظام.",
      topK: "حتى {{n}} مقاطع (يُضبط في «عن هذا الوكيل»).",
      firstStep: "اجعلها الخطوة الأولى",
      isFirst: "هذه هي الخطوة الأولى",
      delete: "حذف الخطوة",
      savedAs: "تُحفظ في الدفتر باسم",
      savedAsHelp: "الاسم الذي تقرأ به الخطوات اللاحقة هذه النتيجة. الفراغ يعني «{{fallback}}».",
      keepOnly: "الاحتفاظ بهذا الجزء فقط من النتيجة",
      keepOnlyHelp: "اتركه فارغًا للاحتفاظ بالنتيجة كاملة.",
      keepOnlyCode: "يُرجع مشغّل الشيفرة النتيجة وسجلاتها؛ «result» يحتفظ بالنتيجة فقط.",
      stdin: "مدخل نصي للشيفرة",
      rawJson: "تعديل كل القيم بصيغة JSON",
      rawJsonHelp: "للمطوّرين: كل قيم هذه الخطوة كما يحفظها ملف الوكيل.",
      techId: "المعرّف التقني",
      repeat: "التكرار بحد أقصى",
      onLimit: "إذا بلغ الحد، انتقل إلى",
      onLimitStop: "توقّف واحتفظ بآخر إجابة",
      retries: "أعد المحاولة",
      retriesHelp: "عدد مرات إعادة محاولة الاستدعاء الفاشل (من 0 إلى 3)، مع الانتظار 2 ثم 4 ثم 8 ثوانٍ. لا يُعاد الاستدعاء الذي رفضته القواعد أو رفضه شخص.",
      failTo: "إذا استمر الفشل، انتقل إلى",
      failToHelp: "يمكن للخطوات اللاحقة قراءة سبب الفشل باسم «ما الذي حدث من خطأ» في نتيجة هذه الخطوة.",
      failToNone: "توقّف هنا وأخبر الشخص (الخطوة التي تقرأ فقط تتابع)",
      onLimitHelp: "وجهة التشغيل عندما تكون هذه الخطوة على وشك العمل مرة أكثر من المسموح، مثل خطوة تجيب بآخر مسودة. إذا أبقيت «توقّف»، ينتهي التشغيل برسالة واضحة ويحتفظ بآخر إجابة أعطاها.",
      onLimitNeedsRepeat: "اضبط «التكرار بحد أقصى» أولاً.",
      askWho: "من يجيب",
      askWhoNone: "الشخص الذي سأل",
      askWhoHelp: "إذا تُرك فارغاً يجيب الشخص الذي بدأ الطلب في المحادثة. أضف أدواراً فيجيب أصحابها فقط في صندوق المراجعة، ولا يرى الشخص الذي سأل السؤال. التشغيلات التي تبدأ دون شخص (جدول زمني) تذهب إلى المعتمدين.",
      askRequesterToo: "يمكن للشخص الذي سأل أن يجيب أيضاً",
      connectTo: "الخطوة التالية",
      connect: "إضافة مسار",
      newValue: "اسم قيمة جديدة",
      addValue: "إضافة قيمة",
      removeValue: "إزالة {{name}}",
      strategy: { merge: "كل نتيجة باسمها", list: "قائمة بكل النتائج", vote: "الإجابة الأكثر تكرارًا" },
      byRule: "بقاعدة",
      byAi: "يقرر الذكاء الاصطناعي",
      addWait: "إضافة خطوة الانتظار",
      noWait: "لا توجد خطوة انتظار بعد: لا يمكن أن يستمر التشغيل بعد هذه الخطوات.",
      continues: "يستمر التشغيل من ‹{{step}}› عند انتهائها كلها.",
      waitOf: "اضبط الخطوات في ‹{{step}}›",
      fixedMakes: "تُنتج هذه الخطوة الشكل نفسه دائمًا؛ لا شيء لضبطه.",
    },
  },
};
